import type { TrainingProjection } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n/useTranslation'
import { formatShortDate } from '../../utils/dateTranslations'
import { getTrainingDisplayStatus, translateTrainingStatus } from '../../utils/statusTranslations'
import { StatusBadge } from '../ui'

interface TrainingProjectionRowProps {
  training: TrainingProjection
  displayNumber: number
  showBorderBottom?: boolean
  isNext?: boolean
  onClick?: () => void
  /** When false, status badges are never shown. Defaults to true. */
  showStatuses?: boolean
}

const HIDDEN_STATUSES = new Set(['COMPLETED', 'UPCOMING', 'ACTIVE'])

function statusVariant(status: string): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (status) {
    case 'CANCELLED':
      return 'error'
    case 'MOVED':
    case 'SKIPPED':
      return 'warning'
    case 'SCHEDULED':
      return 'success'
    default:
      return 'default'
  }
}

/**
 * Shared event/projection row used by training set details and create-set preview.
 */
export function TrainingProjectionRow({
  training,
  displayNumber,
  showBorderBottom = false,
  isNext = false,
  onClick,
  showStatuses = true,
}: TrainingProjectionRowProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const startDate = new Date(training.start)
  const endDate = new Date(training.end)
  const isClickable = !!onClick

  const displayStatuses = training.displayStatus
    ? (training.status === 'MOVED' ? ['MOVED', training.displayStatus] : [training.displayStatus])
    : getTrainingDisplayStatus(training.status, training.start, training.end)
  const visibleStatuses = showStatuses
    ? displayStatuses.filter(status => !HIDDEN_STATUSES.has(status))
    : []

  return (
    <div
      className={`px-4 py-3 flex items-start gap-3 ${isClickable ? 'cursor-pointer active:opacity-70' : ''}`}
      style={showBorderBottom ? { borderBottom: `1px solid ${theme.cardBorder}` } : undefined}
      onClick={onClick}
    >
      <span
        className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
        style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
      >
        {displayNumber}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
            {formatShortDate(startDate, t)}
          </p>
          {isNext && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: 'rgba(234,179,8,0.15)', color: '#ca8a04' }}
            >
              {t.trainingSetDetail.schedule.next}
            </span>
          )}
          {training.isFired && (
            <span className="text-xs font-semibold" style={{ color: '#ef4444' }}>🔥</span>
          )}
        </div>
        <p className="text-xs tabular-nums" style={{ color: theme.text.muted }}>
          {startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          {' – '}
          {endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
        </p>
        {training.comment && (
          <div
            className="mt-2 pt-2"
            style={{ borderTop: `1px solid ${theme.cardBorder}` }}
          >
            <p className="text-xs" style={{ color: theme.text.muted }}>{training.comment}</p>
          </div>
        )}
      </div>

      {visibleStatuses.length > 0 && (
        <div className="flex gap-1 flex-wrap justify-end shrink-0">
          {visibleStatuses.map((status, i) => (
            <StatusBadge
              key={i}
              status={translateTrainingStatus(status, t)}
              variant={statusVariant(status)}
              size="sm"
            />
          ))}
        </div>
      )}
    </div>
  )
}
